/**
 * "Your Claude sign-in expires in N days" — the arithmetic, in one pure place.
 *
 * A `claude.ai` login has a hard expiry that refreshing never extends (the passerelle reads it from
 * the CLI's own `.credentials.json`; see client/passerelle/claude-credentials.ts). Only a NEW
 * sign-in resets it. So this is the one Claude auth state Niobe can warn about *before* it breaks
 * anything — unlike a dead login, which can only ever be reported after the fact.
 *
 * Kept separate from the components because two of them show it (the Settings card and the chat
 * composer) and they must agree to the day, and because "is it worth warning about" is exactly the
 * kind of off-by-one that a unit test should own rather than a render test.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Warn this far ahead — the same three days Claude Code's own startup warning uses. */
export const EXPIRY_WARN_DAYS = 3;

/** How long a dismissal in the chat lasts. Settings is never dismissible. */
export const EXPIRY_DISMISS_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days until the login dies, or `null` when there is nothing to say.
 *
 * `null` covers every "we don't know" as well as "not yet": no `loginExpiresAt` at all (an older
 * passerelle, the macOS Keychain, a long-lived token) and anything further out than
 * {@link EXPIRY_WARN_DAYS}. Rounded UP, so a login with 1.2 days left reads "2 days" rather than
 * understating the time the member has; an expiry already past reads `0`.
 */
export function daysUntilLoginExpiry(loginExpiresAt: number | undefined, now: number = Date.now()): number | null {
  if (typeof loginExpiresAt !== 'number' || !Number.isFinite(loginExpiresAt) || loginExpiresAt <= 0) return null;
  const days = Math.max(0, Math.ceil((loginExpiresAt - now) / DAY_MS));
  return days <= EXPIRY_WARN_DAYS ? days : null;
}
